/**
 * **Brand Tokens — Single Source of Truth.**
 *
 * Cores **canônicas** da marca KM Gestão (navy `#05142b` + champagne
 * gold `#C6A75E` do brand book). Definidas como **constantes TypeScript
 * compartilhadas** pra evitar a regressão histórica de palette default
 * "azul placeholder" (`220 90% 56%`) que sobrescrevia o brand quando
 * `apps/web/src/features/clinic-appearance/palettes.ts` redefinia a
 * paleta `default` à mão.
 *
 * **Como funcionava o bug**: o `PaletteThemeProvider` injeta CSS vars
 * (`--primary`, `--accent`) em runtime no `:root` do documento. Quando
 * a paleta `default` em `palettes.ts` divergia dos valores canônicos do
 * `globals.css`, o runtime sobrescrevia silenciosamente — login,
 * welcome, todas as páginas viravam azul placeholder. Sem warning,
 * sem erro de build, só um "estranho, ficou azul de novo".
 *
 * **Como esta camada previne**: a paleta `default` agora **importa**
 * essas constantes (não duplica os valores). Divergência por
 * construção é impossível. O test `brand-tokens.test.ts` valida que
 * essas constantes batem com `globals.css` (catch drift do CSS).
 *
 * **Onde mais aparece a marca** (mantenha tudo em sincronia se editar):
 *   1. `packages/config-tailwind/src/globals.css` — `:root`
 *      (`--primary`, `--accent`) e `[data-theme="dark"]`
 *   2. ESTE arquivo — `BRAND_TOKENS_LIGHT` / `BRAND_TOKENS_DARK`
 *   3. `apps/web/src/features/clinic-appearance/palettes.ts` —
 *      `PALETTES.default` importa as constantes daqui (não duplica)
 *
 * **Nunca** mude esses valores sem atualizar os 3 lugares + ADR.
 *
 * Formato: strings de **HSL puras** (`H S% L%`) — formato consumido por
 * Tailwind via `hsl(var(--primary))`. NÃO use hex aqui — o lint:tokens
 * do projeto bloqueia hex literal em `.tsx`/`.ts` (regra de DS).
 */

/**
 * Tokens da marca para o **modo light** (background `#FAF9F7` warm).
 * Navy puro do brand book em `primary`, gold em `accent`.
 */
export const BRAND_TOKENS_LIGHT = {
  /** Navy `#05142b` — botões primários, links, item ativo de nav. */
  primary: '216 79% 9%',
  /** Branco-suave (98%) — texto sobre o navy primary. */
  primaryForeground: '0 0% 98%',
  /** Champagne gold `#C6A75E` — accent pontual (badges premium,
   * eyebrows, KPIs em destaque, item ativo da sidebar). */
  accent: '41 49% 57%',
  /** Quase-preto (13%) — texto sobre o accent gold (contraste AA). */
  accentForeground: '0 0% 13%',
} as const;

/**
 * Tokens da marca para o **modo dark** (background quase-preto).
 * Inverte: primary vira branco-suave (legibilidade no dark), accent
 * gold fica mais claro (67%) pra manter contraste, accentForeground
 * vira o navy do brand (gold + navy = combinação assinatura).
 */
export const BRAND_TOKENS_DARK = {
  /** Branco-suave — primary no dark vira a cor "principal" do brand
   * (não dá pra usar navy puro num fundo escuro). */
  primary: '0 0% 95%',
  /** Navy do brand — texto sobre o primary branco. */
  primaryForeground: '0 0% 13%',
  /** Champagne gold mais claro (67% vs 57% no light) — mantém
   * contraste AA num fundo escuro. */
  accent: '41 60% 67%',
  /** Navy do brand — texto sobre o accent gold (combinação
   * navy + gold do brand book). */
  accentForeground: '216 79% 9%',
} as const;

/**
 * Tipo do shape dos tokens da marca — reutilizável por código que quer
 * tipar paletas custom (como `PaletteTokens` em `palettes.ts`).
 */
export type BrandTokens = typeof BRAND_TOKENS_LIGHT;
